// Country dial codes for the booking form's phone field.
//
// This exists because of a real no-show. The booking step had a bare phone input
// with a "(555) 123-4567" placeholder and no country selector, and the API
// assumed any bare 10-digit number was American. An Israeli prospect typed his
// domestic number 055-882-8248, which is exactly ten digits, so it was stored as
// +10558828248 - a number nobody can dial, in a country that is not his. Every
// reminder we would have sent went nowhere, and the first anyone heard of it was
// an empty Zoom room.
//
// So the country is now an explicit choice rather than an assumption. The list
// is deliberately short and hand-picked rather than all ~250 ISO countries: a
// long select is worse on a phone, which is where most of this traffic is, and
// these cover where prospects actually book from. "Other" is the escape hatch
// that lets anyone type a full +… number themselves, so nobody is locked out.

export type PhoneCountry = {
  /** ISO 3166-1 alpha-2, used as the stable option value. */
  code: string
  name: string
  /** Country calling code, digits only, no plus. */
  dial: string
  /** What a national number looks like there, so the placeholder matches the
   * country rather than always showing a US number. */
  example: string
  /** Italy is the well-known exception: it KEEPS its leading zero in the
   * international form, so Rome is +39 06 …, not +39 6 …. Everywhere else the
   * zero is a trunk prefix that is dropped when the dial code goes on. */
  keepsTrunkZero?: true
}

/** Strip formatting to digits only. This does not add or infer a country code. */
export function phoneDigits(value: string | null | undefined): string {
  return (value || "").replace(/\D+/g, "")
}

export const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: "US", name: "United States", dial: "1", example: "(555) 123-4567" },
  { code: "CA", name: "Canada", dial: "1", example: "(555) 123-4567" },
  { code: "GB", name: "United Kingdom", dial: "44", example: "7400 123456" },
  { code: "AU", name: "Australia", dial: "61", example: "412 345 678" },
  { code: "IE", name: "Ireland", dial: "353", example: "85 123 4567" },
  { code: "NZ", name: "New Zealand", dial: "64", example: "21 123 4567" },
  { code: "IL", name: "Israel", dial: "972", example: "55 882 8248" },
  { code: "DE", name: "Germany", dial: "49", example: "1512 3456789" },
  { code: "FR", name: "France", dial: "33", example: "6 12 34 56 78" },
  { code: "NL", name: "Netherlands", dial: "31", example: "6 12345678" },
  { code: "ES", name: "Spain", dial: "34", example: "612 34 56 78" },
  { code: "IT", name: "Italy", dial: "39", example: "06 6982 1234", keepsTrunkZero: true },
  { code: "SE", name: "Sweden", dial: "46", example: "70 123 45 67" },
  { code: "CH", name: "Switzerland", dial: "41", example: "78 123 45 67" },
  { code: "AE", name: "United Arab Emirates", dial: "971", example: "50 123 4567" },
  { code: "SG", name: "Singapore", dial: "65", example: "8123 4567" },
  { code: "IN", name: "India", dial: "91", example: "81234 56789" },
  { code: "BR", name: "Brazil", dial: "55", example: "11 91234 5678" },
  { code: "MX", name: "Mexico", dial: "52", example: "55 1234 5678" },
  { code: "ZA", name: "South Africa", dial: "27", example: "71 123 4567" },
  { code: "OTHER", name: "Other (type full number)", dial: "", example: "+972 55 882 8248" },
]

/** Best guess at the caller's country from the IANA time zone the browser
 * reports, so the selector opens on the right flag instead of always on the US.
 * A guess, not a decision: the selector is visible and the booker can change it,
 * which is the whole point of adding it. */
export function countryFromTimeZone(timeZone: string | null | undefined): string {
  const tz = (timeZone ?? "").trim()
  if (!tz) return "US"
  const byZone: Record<string, string> = {
    "Asia/Jerusalem": "IL",
    "Asia/Tel_Aviv": "IL",
    "Asia/Dubai": "AE",
    "Asia/Singapore": "SG",
    "Asia/Kolkata": "IN",
    "Asia/Calcutta": "IN",
    "Europe/London": "GB",
    "Europe/Dublin": "IE",
    "Europe/Berlin": "DE",
    "Europe/Paris": "FR",
    "Europe/Amsterdam": "NL",
    "Europe/Madrid": "ES",
    "Europe/Rome": "IT",
    "Europe/Stockholm": "SE",
    "Europe/Zurich": "CH",
    "Africa/Johannesburg": "ZA",
    "America/Sao_Paulo": "BR",
    "America/Mexico_City": "MX",
    "Pacific/Auckland": "NZ",
  }
  if (byZone[tz]) return byZone[tz]
  if (tz.startsWith("Australia/")) return "AU"
  // `America/` is the ENTIRE Western hemisphere, not the United States:
  // America/Sao_Paulo, America/Mexico_City, America/Bogota and America/Santiago
  // all live under it. Returning US for the whole prefix preselected the wrong
  // country for every Latin American booker, which is the same shape of mistake
  // this module exists to stop. Only the zones actually mapped above resolve;
  // everything else falls to OTHER, which shows a "type the full number" box
  // rather than quietly guessing a dial code.
  if (tz.startsWith("US/") || tz.startsWith("Canada/")) return "US"
  if (/^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Toronto|Vancouver|Edmonton|Winnipeg|Halifax)$/.test(tz)) {
    return "US"
  }
  return "OTHER"
}

/** Turn a country choice plus what the booker typed into E.164.
 *
 * The rules that matter:
 *  - a country code is NEVER assumed from digit count. "Other", or anything the
 *    booker typed with its own "+", is taken exactly as given. Guessing is what
 *    turned an Israeli number into an American one;
 *  - exactly ONE leading zero is dropped, because it is a national trunk prefix
 *    and +972 0 55… is not a number — except in Italy, which keeps it. */
export function toE164(countryCode: string, national: string): string {
  const typed = national.trim()
  if (typed.startsWith("+")) return `+${phoneDigits(typed.slice(1))}`
  const country = PHONE_COUNTRIES.find((c) => c.code === countryCode)
  // Strip ONE leading zero, not all of them, and not at all in Italy. The zero
  // is a national trunk prefix that one country genuinely keeps; the original
  // `replace(/^0+/, "")` was wrong twice over, eating both zeros of an 00-prefix
  // and the meaningful 0 of an Italian number.
  const raw = phoneDigits(typed)
  const digits = country?.keepsTrunkZero ? raw : raw.replace(/^0/, "")
  // An UNRECOGNISED country fails closed. It used to share the "Other" branch
  // below, so a code we do not know silently produced a bare `+<digits>` with
  // no dial code at all: post `phoneCountry:"XX"` with a UK national number and
  // it became "+7400123456", which passed validation and reached Cal. That is
  // the same class of bug as the no-show, a number nobody can dial built by
  // guessing, so the unknown case returns "" and the caller answers 400.
  // "Other" is a REAL entry with an empty dial, and it keeps working: the
  // booker types the full +… number, which already returned above.
  if (!country) return ""
  if (!country.dial) return digits ? `+${digits}` : ""
  return `+${country.dial}${digits}`
}

/**
 * The E.164 number, readable, for a sentence a human reads.
 *
 * FORMAT AT RENDER TIME, NEVER IN STORAGE. The stored value stays
 * machine-dialable, because `attendeePhoneNumber` feeds
 * `cal_bookings_cache.attendee_phone`, which feeds the Quo text lane and the
 * number a coach dials, and a formatted string there risks a silent send
 * failure (the Israeli no-show class). This function exists because a
 * booking with no meeting link is a PHONE call: the booking confirmation, the
 * reminder ladder and the coach's own nudge all name the number out loud, and
 * `+16507327640` in the middle of a sentence reads like a machine error.
 *
 * North America is the one plan grouped, because it is the only one whose
 * shape is knowable without a full libphonenumber table. Everything else gets
 * its dial code separated and nothing else invented: `+972 558828248` is plain
 * but it is never WRONG, and a made-up grouping on a country we do not model
 * is the same class of guess that produced `+10558828248`.
 *
 * Returns null for anything that is not a usable E.164, so a caller renders
 * no sentence at all rather than a sentence with a blank in it.
 */
export function formatPhoneForDisplay(e164: string | null | undefined): string | null {
  const value = (e164 ?? "").trim()
  if (!value || !isValidE164(value)) return null
  const digits = value.slice(1)
  if (digits.startsWith("1") && digits.length === 11) {
    const national = digits.slice(1)
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
  }
  // Longest dial code first, so +1 never shadows a longer code and +97 never
  // shadows +972.
  const dial = PHONE_COUNTRIES.map((c) => c.dial)
    .filter((d) => d && digits.startsWith(d))
    .sort((a, b) => b.length - a.length)[0]
  if (!dial) return value
  return `+${dial} ${digits.slice(dial.length)}`
}

/** One predicate, called by the form AND the API.
 *
 * They used to disagree: the client judged the national digits and the server
 * judged the finished E.164, so a long national number could pass the button
 * and then 400 on submit, which reads to the prospect as the site being broken.
 * Judging the finished string is the correct half, because a bare length check
 * on raw digits is what accepted `+10558828248` in the first place. */
export function isValidE164(e164: string): boolean {
  if (!e164.startsWith("+")) return false
  const digits = e164.slice(1)
  if (digits.length < 8 || digits.length > 15) return false
  if (digits.startsWith("1")) {
    // North America is the one plan strict enough to check cheaply, and it is
    // exactly where a foreign national number lands when the selector is left
    // untouched. Neither the area code nor the exchange may begin 0 or 1.
    const national = digits.slice(1)
    return national.length === 10 && !/^[01]/.test(national) && !/^[01]/.test(national.slice(3))
  }
  return true
}
